<template>
  <b-modal
    ref="modal"
    :title="title"
    :size="mode === 'checkin' || mode === 'cancel' ? 'lg' : 'md'"
    hide-footer
    centered
    @hidden="$emit('closed')">
    <div v-if="context" class="p-50">
      <!-- Contexto: a mesma linha em todos os modos, para o usuário nunca
           perder de vista qual ocorrência está prestes a mudar. -->
      <div class="border rounded p-1 mb-1">
        <div class="flex justify-between mb-25">
          <small class="text-muted">{{ $t('lesson.agenda.occurrence') }}</small>
          <small class="font-weight-bold">{{ occurrenceLabel }}</small>
        </div>
        <div class="flex justify-between">
          <small class="text-muted">{{ $t('lesson.agenda.occupancy') }}</small>
          <small>{{ context.slot.occupied }}/{{ context.slot.capacity === null ? '∞' : context.slot.capacity }}</small>
        </div>
      </div>

      <!-- CHECK-IN -->
      <template v-if="mode === 'checkin'">
        <div class="flex items-center justify-between mt-2 mb-50">
          <span>{{ $t('mural.checkin.hint') }}</span>
          <span class="font-weight-bold">
            {{ $t('mural.checkin.count', { present: present.length, total: context.slot.students.length }) }}
          </span>
        </div>
        <div
          v-for="student in context.slot.students"
          :key="student.scheduled_event_id"
          class="mural-check"
          @click="togglePresent(student.scheduled_event_id)">
          <div class="flex-grow" style="min-width: 0;">
            <div class="mural-check__name">{{ student.name }}</div>
            <!-- Entrada pela portaria: é diferente hesitar sobre alguém que
                 ninguém viu chegar e sobre alguém cujo ingresso foi lido às 18:52. -->
            <div v-if="student.gate_check_in" class="mural-check__gate">
              <feather-icon icon="CheckSquareIcon" size="12" class="mr-25" />
              <small>{{ $t('mural.checkin.gate_entry', { time: student.gate_check_in.at, channel: student.gate_check_in.channel_label }) }}</small>
            </div>
            <div v-else class="text-muted"><small>{{ student.email }}</small></div>
          </div>
          <b-badge :variant="student.status === 'DO' ? 'light-success' : 'light-primary'" pill class="mr-1">
            {{ student.origin_label || '—' }}
          </b-badge>
          <span class="mural-check__box" :class="{ 'mural-check__box--on': isPresent(student.scheduled_event_id) }">
            <feather-icon icon="CheckIcon" size="12" />
          </span>
        </div>
        <b-alert show variant="secondary" class="p-1 mt-1 mb-0">
          <small>{{ $t('mural.checkin.absent_note') }}</small>
        </b-alert>
      </template>

      <!-- REMARCAR -->
      <template v-if="mode === 'reschedule'">
        <b-form-group :label="$t('mural.reschedule.new_slot')" class="mt-2">
          <b-form-select v-model="targetSlot" :options="slotOptions" :disabled="loadingSlots" />
          <small v-if="!loadingSlots && !slotOptions.length" class="text-danger">
            {{ $t('mural.reschedule.no_slots') }}
          </small>
        </b-form-group>
        <b-form-group :label="$t('mural.reschedule.reason')">
          <b-form-select v-model="reason" :options="reasonOptions" />
        </b-form-group>
        <b-alert show variant="warning" class="p-1 mb-0">
          <div class="flex items-start">
            <feather-icon icon="AlertTriangleIcon" class="mr-50 flex-shrink-0" />
            <small>{{ $t('mural.reschedule.warning') }}</small>
          </div>
        </b-alert>
      </template>

      <!-- AJUSTAR VAGAS -->
      <template v-if="mode === 'capacity'">
        <b-form-group :label="$t('lesson.agenda.new_capacity')" class="mt-2">
          <b-form-input
            type="number"
            min="1"
            max="999"
            v-model.number="capacity"
            :placeholder="`${context.slot.capacity === null ? '' : context.slot.capacity}`" />
        </b-form-group>
        <small class="text-muted d-block mb-1">{{ $t('lesson.agenda.capacity_hint') }}</small>
        <b-alert show variant="warning" class="p-1 mb-1" v-if="capacity && capacity < context.slot.occupied">
          <small>{{ $t('lesson.agenda.capacity_below_occupancy_warning', { occupied: context.slot.occupied }) }}</small>
        </b-alert>
        <b-form-group :label="$t('lesson.agenda.reason')">
          <b-form-input v-model="reason" :placeholder="$t('lesson.agenda.reason_placeholder')" />
        </b-form-group>
      </template>

      <!-- BLOQUEAR -->
      <template v-if="mode === 'block'">
        <b-alert show variant="warning" class="p-1 mt-2 mb-1">
          <div class="flex items-start">
            <feather-icon icon="AlertTriangleIcon" class="mr-50 flex-shrink-0" />
            <small>{{ $t('lesson.agenda.block_hint') }}</small>
          </div>
        </b-alert>
        <b-form-group :label="$t('lesson.agenda.reason')">
          <b-form-input v-model="reason" :placeholder="$t('lesson.agenda.reason_placeholder')" />
        </b-form-group>
      </template>

      <!-- AUTORIZAR ALUNO -->
      <template v-if="mode === 'grant'">
        <b-alert show variant="warning" class="p-1 mt-2 mb-1">
          <div class="flex items-start">
            <feather-icon icon="AlertTriangleIcon" class="mr-50 flex-shrink-0" />
            <small>{{ $t('schedule_event.seat_grant_hint') }}</small>
          </div>
        </b-alert>
        <b-form-group :label="$t('schedule_event.seat_grant_student')">
          <!-- model="User" e não "Student": a autorização é nominal e aponta para
               public.users, que é também quem ocupa o assento. -->
          <select-suggestion
            ref="grantStudent"
            column="name,email"
            model="User"
            v-model="grantStudent"
            :label="null"
            :appendClearOption="true"
            placeholderText="Digite o nome ou e-mail do aluno"
            :itemTextFormat="studentItemTextFormat" />
        </b-form-group>
        <b-form-group :label="$t('schedule_event.seat_grant_scope')">
          <b-form-radio-group
            v-model="grantScope"
            :options="grantScopeOptions"
            stacked
            value-field="value"
            text-field="label" />
        </b-form-group>
        <b-form-group :label="$t('lesson.agenda.reason')">
          <b-form-input v-model="reason" :placeholder="$t('lesson.agenda.reason_placeholder')" />
        </b-form-group>
      </template>

      <!-- CANCELAR AULA DO DIA -->
      <template v-if="mode === 'cancel'">
        <b-form-group :label="$t('cancellation.club_cancel_reason')" class="mt-2">
          <b-form-select v-model="reason" :options="reasonOptions" class="required" />
        </b-form-group>

        <!-- O impacto financeiro só é montado para quem pode ver valores; o
             professor não chega aqui (não tem cancellation_requests.edit), mas a
             guarda fica explícita. -->
        <div class="border rounded p-1 mb-1" v-if="impact && canSeeMoney">
          <div class="font-weight-bold mb-50">{{ $t('cancellation.club_cancel_impact') }}</div>
          <div class="flex justify-between mb-25">
            <small class="text-muted">{{ $t('cancellation.club_cancel_students') }}</small>
            <small>{{ impact.affected_students }}</small>
          </div>
          <div class="flex justify-between mb-25">
            <small class="text-muted">{{ $t('cancellation.club_cancel_refund') }}</small>
            <small class="hb-money">{{ money(impact.full_refund_total) }}</small>
          </div>
          <div class="flex justify-between mb-25">
            <small class="text-muted">{{ $t('cancellation.club_cancel_transfer') }}</small>
            <small class="hb-money">{{ money(impact.settled_transfer_reversal) }}</small>
          </div>
          <div class="flex justify-between">
            <small class="text-muted">{{ $t('cancellation.club_cancel_fee') }}</small>
            <small class="hb-money">{{ money(impact.hubee_fee_returned) }}</small>
          </div>
        </div>

        <div class="flex justify-between items-center border rounded p-1 mb-1">
          <div>
            <div>{{ $t('cancellation.club_cancel_mass_reschedule') }}</div>
            <small class="text-muted">{{ $t('cancellation.club_cancel_mass_hint') }}</small>
          </div>
          <b-form-checkbox v-model="offerReschedule" switch />
        </div>

        <b-alert show variant="danger" class="p-1 mb-0">
          <div class="flex items-start">
            <feather-icon icon="AlertTriangleIcon" class="mr-50 flex-shrink-0" />
            <small>{{ $t('lesson.agenda.cancel_warning') }}</small>
          </div>
        </b-alert>
      </template>

      <!-- Rodapé -->
      <div class="flex justify-end mt-2" style="gap: .6rem;">
        <b-button variant="outline-secondary" @click="close">{{ $t('mural.actions.back') }}</b-button>
        <b-button :variant="confirmVariant" :disabled="!canConfirm" @click="confirm">
          {{ confirmLabel }}
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  BAlert,
  BButton,
  BBadge,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormRadioGroup,
  BFormSelect,
  BModal,
} from 'bootstrap-vue'
import CancellationRequestService from '@/services/api/CancellationRequestService'
import LessonService from '@/services/api/LessonService'
import SelectSuggestion from '@/components/SelectSuggestion.vue'
import { currencyFormatter } from '@/util/Util'
import { parseDate, slotStatus, toDateString } from './muralAgenda'

const RESCHEDULE_WINDOW_DAYS = 28

/**
 * Os seis modais de ação da ocorrência.
 *
 * Quatro deles (vagas, bloqueio, autorização, cancelamento) batem nos mesmos
 * endpoints que a tela "Agenda da aula" já usava; os outros dois (check-in e
 * remarcação em massa) são novos e movem a turma inteira numa transação só.
 */
export default {
  name: 'MuralActionModal',
  components: {
    BAlert,
    BButton,
    BBadge,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormRadioGroup,
    BFormSelect,
    BModal,
    SelectSuggestion,
  },
  data: () => ({
    service: null,
    cancellationService: null,
    mode: null,
    context: null,
    reason: '',
    capacity: null,
    offerReschedule: true,
    impact: null,
    grantStudent: null,
    grantScope: 'OCC',
    present: [],
    targetSlot: null,
    slotOptions: [],
    loadingSlots: false,
    submitting: false,
  }),
  computed: {
    canSeeMoney() {
      return this.$acl.hasPermission('financial.dashboard.menu')
    },
    reasonOptions() {
      return [
        'Professor indisponível',
        'Manutenção do espaço',
        'Clima',
        'Baixa ocupação',
        'Outro',
      ]
    },
    grantScopeOptions() {
      const labels = this.$t('schedule_event.seat_grant_scope_labels')
      return [
        { value: 'OCC', label: labels.OCC },
        { value: 'SLT', label: labels.SLT },
      ]
    },
    title() {
      const titles = {
        checkin: this.$t('mural.checkin.title'),
        reschedule: this.$t('mural.reschedule.title'),
        capacity: this.$t('lesson.agenda.adjust_capacity'),
        block: this.$t('lesson.agenda.block'),
        grant: this.$t('schedule_event.seat_grant'),
        cancel: this.$t('lesson.agenda.cancel_occurrence'),
      }
      const name = this.context ? this.context.lesson.name : ''
      return `${titles[this.mode] || ''}${name ? ` · ${name}` : ''}`
    },
    occurrenceLabel() {
      if (!this.context) return ''
      const [year, month, day] = this.context.date.split('-')
      return `${day}/${month}/${year} · ${this.context.slot.startTime} - ${this.context.slot.endTime}`
    },
    studentCount() {
      return this.context ? this.context.slot.students.length : 0
    },
    confirmLabel() {
      const labels = {
        checkin: this.$t('mural.checkin.confirm'),
        reschedule: this.$t('mural.reschedule.confirm', { count: this.studentCount }),
        capacity: this.$t('lesson.agenda.adjust_capacity'),
        block: this.$t('lesson.agenda.block'),
        grant: this.$t('schedule_event.seat_grant'),
        cancel: this.$t('cancellation.club_cancel_confirm', {
          count: this.impact ? this.impact.affected_students : this.studentCount,
        }),
      }
      return labels[this.mode] || ''
    },
    confirmVariant() {
      return {
        checkin: 'success',
        reschedule: 'primary',
        capacity: 'warning',
        block: 'dark',
        grant: 'success',
        cancel: 'danger',
      }[this.mode] || 'primary'
    },
    canConfirm() {
      if (this.submitting) return false
      if (this.mode === 'checkin') return this.present.length > 0
      if (this.mode === 'reschedule') return !!this.targetSlot && !!this.reason
      if (this.mode === 'capacity') return !!this.capacity
      if (this.mode === 'grant') return !!(this.grantStudent && this.grantStudent.id)
      if (this.mode === 'cancel') return !!this.reason
      return true
    },
  },
  methods: {
    open(mode, context) {
      this.mode = mode
      this.context = context
      this.impact = null
      this.submitting = false
      this.capacity = context.slot.capacity_source === 'override' ? context.slot.capacity : null
      this.reason = (mode === 'cancel' || mode === 'reschedule')
        ? this.reasonOptions[0]
        : (context.slot.reason || '')
      this.offerReschedule = true
      // Autorizar é sempre decisão nova; herdar o aluno da vez anterior liberaria
      // vaga para quem o professor não escolheu agora.
      this.grantStudent = null
      this.grantScope = 'OCC'
      // Check-in começa com todos marcados: a exceção é a falta, não a presença.
      this.present = mode === 'checkin'
        ? context.slot.students.map(student => student.scheduled_event_id)
        : []
      this.targetSlot = null
      this.slotOptions = []

      this.$refs.modal.show()

      if (mode === 'cancel') this.loadImpact()
      if (mode === 'reschedule') this.loadSlotOptions()
    },
    close() {
      this.$refs.modal.hide()
    },
    isPresent(id) {
      return this.present.includes(id)
    },
    togglePresent(id) {
      const index = this.present.indexOf(id)
      if (index >= 0) this.present.splice(index, 1)
      else this.present.push(id)
    },
    money(value) {
      return currencyFormatter(Number(value || 0))
    },
    studentItemTextFormat(model) {
      return model && model.email ? `${model.name} (${model.email})` : (model ? model.name : '')
    },
    loadImpact() {
      this.cancellationService.clubCancelImpact({
        lesson_id: this.context.lesson.id,
        date: this.context.date,
        start_time: this.context.slot.startTime,
        end_time: this.context.slot.endTime,
      }).then(
        impact => { this.impact = impact },
        () => {},
      )
    },
    /**
     * Destinos possíveis: ocorrências futuras DESTA aula nas próximas semanas.
     * Vem do endpoint por aula (não da agenda do Mural) porque a janela visível
     * pode ser de um dia só, e o destino natural quase sempre é a semana seguinte.
     */
    loadSlotOptions() {
      this.loadingSlots = true
      const from = toDateString(new Date())

      this.service.getOccurrences(this.context.lesson.id, from, RESCHEDULE_WINDOW_DAYS).then(
        response => {
          const origin = `${this.context.date}|${this.context.slot.startTime}`
          const now = Date.now()
          const options = []

          ;(response.days || []).forEach(day => {
            (day.slots || []).forEach(slot => {
              if (`${day.date}|${slot.startTime}` === origin) return

              const status = slotStatus(slot)
              if (status === 'cancelled' || status === 'blocked') return

              const date = parseDate(day.date)
              const [hour, minute] = slot.startTime.split(':').map(Number)
              date.setHours(hour, minute || 0, 0, 0)
              if (date.getTime() <= now) return

              options.push({
                value: { date: day.date, startTime: slot.startTime, endTime: slot.endTime },
                text: `${this.labelOf(day.date)} · ${slot.startTime} - ${slot.endTime}`,
              })
            })
          })

          this.slotOptions = options
          this.targetSlot = options.length ? options[0].value : null
        },
        () => { this.slotOptions = [] },
      ).finally(() => { this.loadingSlots = false })
    },
    labelOf(date) {
      const parsed = parseDate(date)
      const weekdays = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb']
      const day = String(parsed.getDate()).padStart(2, '0')
      const month = String(parsed.getMonth() + 1).padStart(2, '0')
      return `${weekdays[parsed.getDay()]}, ${day}/${month}`
    },
    confirm() {
      const handlers = {
        checkin: this.submitCheckin,
        reschedule: this.submitReschedule,
        capacity: () => this.saveOverride({ capacity: this.capacity, status: 'AC' }, 'capacity'),
        block: () => this.saveOverride({ status: 'BL' }, 'blocked'),
        grant: this.submitGrant,
        cancel: this.submitCancel,
      }

      const handler = handlers[this.mode]
      if (handler) handler()
    },
    submitCheckin() {
      this.run(
        this.service.checkinOccurrence(this.context.lesson.id, {
          ...this.occurrencePayload(),
          scheduled_event_ids: this.present,
        }),
        () => this.$t('mural.checkin.success', { count: this.present.length }),
      )
    },
    submitReschedule() {
      this.run(
        this.service.rescheduleOccurrence(this.context.lesson.id, {
          ...this.occurrencePayload(),
          new_date: this.targetSlot.date,
          new_start_time: this.targetSlot.startTime,
          new_end_time: this.targetSlot.endTime,
          reason: this.reason,
        }),
        () => this.$t('mural.reschedule.success', { count: this.studentCount }),
      )
    },
    submitGrant() {
      this.run(
        this.service.createSeatGrant(this.context.lesson.id, {
          user_id: this.grantStudent.id,
          scope: this.grantScope,
          date: this.context.date,
          start_time: this.context.slot.startTime,
          end_time: this.context.slot.endTime,
          reason: this.reason || null,
        }),
        () => this.$t('mural.toast.granted'),
      )
    },
    submitCancel() {
      this.run(
        this.service.cancelOccurrence(this.context.lesson.id, {
          ...this.occurrencePayload(),
          reason: this.reason,
          offer_reschedule: this.offerReschedule,
        }),
        () => this.$t('mural.toast.cancelled', { count: this.studentCount }),
      )
    },
    saveOverride(extra, toastKey) {
      this.run(
        this.service.saveOccurrenceOverride(this.context.lesson.id, {
          ...this.occurrencePayload(),
          reason: this.reason || null,
          ...extra,
        }),
        () => this.$t(`mural.toast.${toastKey}`),
      )
    },
    occurrencePayload() {
      return {
        date: this.context.date,
        start_time: this.context.slot.startTime,
        end_time: this.context.slot.endTime,
      }
    },
    run(promise, messageFn) {
      this.submitting = true
      this.$vs.loading()

      promise.then(
        () => {
          this.notifySuccess(this.$vs, messageFn())
          this.close()
          this.$emit('saved')
        },
        error => this.notifyErrors(this.$vs, error),
      ).finally(() => {
        this.submitting = false
        this.$vs.loading.close()
      })
    },
  },
  beforeMount() {
    this.service = LessonService.build(this.$vs)
    this.cancellationService = CancellationRequestService.build(this.$vs)
  },
}
</script>

<style lang="scss" scoped>
/* Entrada registrada na portaria — verde, para o professor achar de relance
   quem já está no local sem precisar ler nome por nome. */
.mural-check__gate {
  display: flex;
  align-items: center;
  color: #28c76f;
}

.mural-check {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.6rem 0.25rem;
  border-bottom: 1px solid var(--border-color);
  cursor: pointer;

  &:hover {
    background: var(--surface-hover);
  }

  &__name {
    font-size: 0.875rem;
    font-weight: var(--fw-semibold);
    color: var(--text-heading);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__box {
    width: 24px;
    height: 24px;
    flex: none;
    border-radius: var(--radius-pill);
    border: 2px solid var(--input-border);
    background: #fff;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    svg {
      opacity: 0;
    }

    &--on {
      border-color: var(--success);
      background: var(--success);

      svg {
        opacity: 1;
      }
    }
  }
}
</style>
